import { Link } from 'react-router-dom';
import type { Article } from '../../lib/content';
import { formatViews, getCategoryByKey } from '../../lib/content';

interface ArticleCardProps {
  article: Article;
  variant?: 'list' | 'grid' | 'compact';
  rank?: number;
}

export default function ArticleCard({ article, variant = 'list', rank }: ArticleCardProps) {
  const category = getCategoryByKey(article.category);

  if (variant === 'compact') {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex items-start gap-3 py-3 border-b border-background-200 last:border-0"
      >
        {typeof rank === 'number' && (
          <span
            className={`shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-xs font-heading font-bold ${
              rank <= 3 ? 'bg-accent-500 text-white' : 'bg-background-200 text-foreground-600'
            }`}
          >
            {rank}
          </span>
        )}
        <div className="min-w-0">
          <h3 className="text-sm font-medium text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <div className="mt-1.5 flex items-center gap-3 text-xs text-foreground-500">
            <span>{article.publishedAt.slice(5, 10)}</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line" />
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'grid') {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex flex-col rounded-lg overflow-hidden border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
      >
        <div className="w-full h-44 overflow-hidden bg-background-100">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - WWW.17C.COM`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-4 flex flex-col flex-1">
          {category && (
            <span className="self-start text-xs font-medium text-accent-700 bg-accent-50 px-2 py-0.5 rounded-md mb-2">
              {category.name}
            </span>
          )}
          <h3 className="font-heading font-semibold text-base text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2 flex-1">
            {article.summary}
          </p>
          <div className="mt-3 flex items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {article.publishedAt.slice(0, 10)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line" />
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex gap-4 py-4 border-b border-background-200 last:border-0"
    >
      <div className="flex-1 min-w-0">
        <h3 className="font-heading font-semibold text-base md:text-lg text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2 hidden sm:block">
          {article.summary}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
          {category && (
            <span className="text-accent-700 bg-accent-50 px-2 py-0.5 rounded-md font-medium">
              {category.name}
            </span>
          )}
          <span className="flex items-center gap-1">
            <i className="ri-time-line" />
            {article.publishedAt.slice(0, 10)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-fire-line" />
            {formatViews(article.views)}
          </span>
        </div>
      </div>
      <div className="shrink-0 w-28 h-20 sm:w-40 sm:h-28 rounded-lg overflow-hidden bg-background-100">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - WWW.17C.COM`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
    </Link>
  );
}